import React, { useState } from 'react';
import { IAffiliationPopupDeal } from 'components/deal-affiliation-popup/types';
import styled from '@emotion/styled';
import { useImageUrlBuilder } from 'hooks/useImageUrlBuilder';
import { DEFAULT_PLACEHOLDER_IMAGE } from 'utils/gallery';
import { Route } from 'config/routes';
import { Link } from 'components/link';
import { Text } from 'ds/components/typography';
import { useLocale } from 'locale';
import { ResetButton as Button } from 'ds/components/button/ResetButton';
import ArrowIcon from 'assets/svg/agent-console/arrow-down.svg';
import { typographyFontSizeCfg, typographyLineHeightCfg } from 'ds/components/typography/types';
import StatusLabel from 'components/deal-affiliation-popup/status-label';
import { DealAffiliationStatus } from 'utils/entities';


const IMAGE_HEIGHT = 152;
const EMPTY = '-';

const DealInfoRoot = styled.div`
  ${({ theme }) => `
    background: #FAFBFD;
    margin: -${theme.spacing(3)} -${theme.spacing(3)} 0 -${theme.spacing(3)};
    padding: ${theme.spacing(2)} ${theme.spacing(3)};
    color: ${theme.colors.neutrals.grey1};
    a, a:hover, a:active {
      text-decoration: none;
      color: ${theme.colors.brand.main};
    }
  `}
`;

const DealImageWrapper = styled.div`
  ${({ theme }) => `
    min-height: ${IMAGE_HEIGHT}px;
    border-radius: ${theme.borderRadius.tiny};
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;

    img {
      object-fit: cover;
      width: 100%;
      height: 100%;
    }
  `}
`;

const ExpandMenuWrapper = styled.div<{ collapsed: boolean; }>`
  ${({ collapsed, theme }) => `
    display: flex;
    justify-content: space-between;
    padding: ${theme.spacing(1)} 0;

    button {
      height: ${typographyLineHeightCfg.text}px;

      > svg {
      ${collapsed ? 'transform: rotate(180deg);' : ''}
    }
    }

    a {
      font-size: ${typographyFontSizeCfg.text}px;
      line-height: ${typographyLineHeightCfg.text}px;
    }
  `}
`;

const PropertyDetails = styled.div`
  ${({ theme }) => `
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(1)};
  `}
`;

const PropertyDetailsItem = styled(Text)`
  ${({ theme }) => `
    gap: ${theme.spacing(0.5)};
    display: flex;
  `}
`;

const PropertyDetailsName = styled.div`
  ${({ theme }) => `
    color: ${theme.colors.neutrals.grey2};
  `}
`;

const AffiliationStatusWrapper = styled.div`
  display: inline-block;
`;

interface DealInfoProps {
  deal: IAffiliationPopupDeal;
  affiliationStatus?: DealAffiliationStatus;
}

export const MobileDealInfo: React.FC<DealInfoProps> = ({ deal, affiliationStatus }) => {
  const constructImageURI = useImageUrlBuilder();
  const [ collapsed, setCollapsed ] = useState(true);
  const { t, formatDate, formatMoney, formatArea } = useLocale();

  const parsedArea = deal.area ? parseInt(deal.area, 10) : null;
  const pricePerSqMeter = parsedArea ? Math.floor(deal.price / parsedArea) : null;
  const imageUrl = constructImageURI(DEFAULT_PLACEHOLDER_IMAGE, { height: IMAGE_HEIGHT });

  const { streetName, streetNumber, neighbourhood, city } = deal.structuredAddress;
  const address = `${streetName ? streetName : ''} ${streetNumber ? streetNumber + ',' : ''} ${neighbourhood ? neighbourhood + ',' : ''}\n ${city ? city : ''}`;

  const toggleExpand = () => setCollapsed(!collapsed);

  return (
    <DealInfoRoot>
      <DealImageWrapper>
        <img src={imageUrl} alt={address} />
      </DealImageWrapper>
      <ExpandMenuWrapper collapsed={collapsed}>
        <Link target="_blank" routeName={Route.Sold} routeParams={{ id: deal.soldId }}>
          <Text weight="bold" data-auto="deal-address">
            {address}
          </Text>
        </Link>
        <Button onClick={toggleExpand}><ArrowIcon /></Button>
      </ExpandMenuWrapper>
      {affiliationStatus ? (
        <AffiliationStatusWrapper>
          <StatusLabel status={affiliationStatus} />
        </AffiliationStatusWrapper>
      ) : null}
      {collapsed ? null : (
        <PropertyDetails>
          <PropertyDetailsItem>
            <PropertyDetailsName>{t('dealAffiliationPopup.property.transactionDate')}</PropertyDetailsName>
            <Text>{deal.dealDate ? formatDate(deal.dealDate) : EMPTY}</Text>
          </PropertyDetailsItem>
          <PropertyDetailsItem>
            <PropertyDetailsName>{t('dealAffiliationPopup.property.price')}</PropertyDetailsName>
            <Text>{deal.price ? formatMoney(deal.price) : EMPTY}</Text>
          </PropertyDetailsItem>
          <PropertyDetailsItem>
            <PropertyDetailsName>{t('dealAffiliationPopup.property.constructionYear')}</PropertyDetailsName>
            <Text>{deal.constructionYear || EMPTY}</Text>
          </PropertyDetailsItem>
          <PropertyDetailsItem>
            <PropertyDetailsName>{t('dealAffiliationPopup.property.propertyArea')}</PropertyDetailsName>
            <Text>{parsedArea ? formatArea(parsedArea) : EMPTY}</Text>
          </PropertyDetailsItem>
          <PropertyDetailsItem>
            <PropertyDetailsName>{t('dealAffiliationPopup.property.pricePerSquareMeter')}</PropertyDetailsName>
            <Text>{pricePerSqMeter ? formatMoney(pricePerSqMeter) : EMPTY}</Text>
          </PropertyDetailsItem>
          <PropertyDetailsItem>
            <PropertyDetailsName>{t('dealAffiliationPopup.property.rooms')}</PropertyDetailsName>
            <Text>{deal.rooms || EMPTY}</Text>
          </PropertyDetailsItem>
          <PropertyDetailsItem>
            <PropertyDetailsName>{t('dealAffiliationPopup.property.floor')}</PropertyDetailsName>
            {deal.floor ? t('filters.floorLabels', { floor: deal.floor }) : EMPTY}
          </PropertyDetailsItem>
        </PropertyDetails>
      )}
    </DealInfoRoot>
  );
};
